import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const translations = {
  fr: {
    nav: {
      features: "Fonctionnalités",
      how: "Comment ça marche",
      pricing: "Tarifs",
      faq: "FAQ",
      login: "Connexion",
      dashboard: "Tableau de bord",
      logout: "Déconnexion",
      accounts: "Comptes Drive",
      settings: "Paramètres",
      jobs: "Sauvegardes",
      billing: "Plan & facturation",
      admin: "Back-office",
    },
    common: {
      loading: "Chargement…",
      save: "Enregistrer",
      cancel: "Annuler",
      delete: "Supprimer",
      edit: "Modifier",
      create: "Créer",
      back: "Retour",
      next: "Suivant",
      previous: "Précédent",
      page: "Page",
      yes: "Oui",
      no: "Non",
      confirm: "Confirmer",
      none: "Aucun",
      search: "Rechercher",
      copied: "Copié",
      skipped: "Ignorés",
      failed: "Échec",
      success: "Succès",
      partial: "Partiel",
      running: "En cours",
      idle: "Au repos",
      enabled: "Activée",
      disabled: "Désactivée",
      enable: "Activer",
      disable: "Désactiver",
      open: "Ouvrir",
      close: "Fermer",
      view: "Voir",
      browse: "Parcourir",
      root: "Racine du Drive",
    },
    landing: {
      tagline: "INFRASTRUCTURE DE SAUVEGARDE CLOUD",
      heroTitle: "Automatisez votre Infrastructure Cloud.",
      heroSubtitle:
        "Sauvegardes cloud planifiées et invisibles entre Google Drive, OneDrive et Dropbox. Incrémentales, fiables et sécurisées — sans laisser un PC allumé.",
      ctaPrimary: "Démarrer la synchronisation",
      ctaSecondary: "Voir les fonctionnalités",
      featuresTitle: "Une mécanique de précision pour vos données",
      featuresSubtitle:
        "SAVEBOX déplace vos fichiers d'un cloud à un autre avec une précision d'horlogerie. Google Drive, OneDrive, Dropbox, Wasabi S3 — sans intermédiaire.",
      f1Title: "Planification autonome",
      f1Desc:
        "Exécutez des sauvegardes chaque heure, jour, semaine ou mois. Pas besoin d'ordinateur allumé : notre serveur travaille pour vous.",
      f2Title: "Copie incrémentale",
      f2Desc:
        "Seuls les fichiers modifiés sont transférés. Empreintes MD5 comparées entre source et destination pour une efficacité maximale.",
      f3Title: "Versioning intelligent",
      f3Desc:
        "Conservez les versions précédentes lors des écrasements. Vos données historisées, jamais perdues.",
      f4Title: "Filtres précis",
      f4Desc:
        "Filtrez par extension, taille, ou nom de dossier. Sauvegardez exactement ce dont vous avez besoin.",
      f5Title: "Cloud à cloud direct",
      f5Desc:
        "Transferts serveur-à-serveur entre Google Drive, OneDrive, Dropbox et Wasabi S3. Aucune donnée ne transite par votre PC.",
      f6Title: "Historique complet",
      f6Desc:
        "Journaux détaillés pour chaque exécution. Fichiers copiés, ignorés, échoués, et octets transférés.",
      howTitle: "Trois étapes. Tout est automatique.",
      step1: "Connectez vos comptes cloud",
      step1Desc: "Liez vos comptes Google Drive, OneDrive ou Dropbox en un clic OAuth. Source et destination, comme vous voulez.",
      step2: "Configurez la sauvegarde",
      step2Desc: "Choisissez les dossiers, la fréquence (heure, jour, semaine, mois) et les filtres.",
      step3: "Laissez tourner. C'est tout.",
      step3Desc: "SAVEBOX exécute vos jobs en arrière-plan. Vous recevez l'historique dans votre tableau de bord.",
      ctaBottomTitle: "Prêt à éteindre votre PC ?",
      ctaBottomSubtitle: "Démarrez avec SAVEBOX en moins de deux minutes.",
      faqTitle: "Questions fréquentes",
      faq1Q: "Mes fichiers transitent-ils par votre serveur ?",
      faq1A:
        "Le contenu de vos fichiers est téléchargé temporairement par notre serveur pour être ré-uploadé vers la destination. Aucune donnée n'est conservée après transfert.",
      faq2Q: "Les sauvegardes consomment-elles mon quota cloud ?",
      faq2A:
        "Oui — votre compte de destination (Drive B, OneDrive, Dropbox ou bucket Wasabi) doit avoir suffisamment d'espace libre pour accueillir les fichiers de votre compte source.",
      faq3Q: "Quels services cloud sont supportés ?",
      faq3A:
        "Aujourd'hui : Google Drive, Microsoft OneDrive, Dropbox et Wasabi (stockage S3). Vous pouvez librement mixer les sources et destinations entre ces services.",
      faq4Q: "Que se passe-t-il si un fichier change ?",
      faq4A:
        "En mode incrémental, SAVEBOX détecte les modifications via la somme MD5 et met à jour uniquement les fichiers modifiés.",
      footerRights: "Tous droits réservés.",
    },
    auth: {
      signInGoogle: "Continuer avec Google",
      signInMicrosoft: "Continuer avec Microsoft",
      signInDemo: "Essayer en mode démo",
      signingIn: "Connexion en cours…",
      welcome: "Bienvenue sur SAVEBOX",
      welcomeSub: "Connectez-vous pour gérer vos sauvegardes cloud-à-cloud.",
      oauthDisabled:
        "OAuth n'est pas encore configuré. Utilisez le mode démo pour explorer l'interface.",
      orSeparator: "ou",
      providerHint: "Choisissez votre fournisseur principal — vous pourrez connecter l'autre cloud plus tard.",
    },
    dashboard: {
      title: "Tableau de bord",
      subtitle: "Pilotez toutes vos sauvegardes en un coup d'œil.",
      newJob: "Nouvelle sauvegarde",
      noJobs: "Aucune sauvegarde configurée pour le moment.",
      noJobsSub: "Créez votre première sauvegarde cloud-à-cloud.",
      jobs: "Jobs",
      activeJobs: "Jobs actifs",
      accounts: "Comptes",
      totalFiles: "Fichiers sauvegardés",
      totalData: "Données transférées",
      recentActivity: "Activité récente",
      noActivity: "Aucune activité pour le moment.",
      runNow: "Exécuter",
      viewDetails: "Détails",
      lastRun: "Dernière exécution",
      nextRun: "Prochaine exécution",
      never: "Jamais",
      now: "À l'instant",
      onDemand: "Sur demande",
    },
    accountsPage: {
      title: "Comptes cloud",
      subtitle: "Connectez plusieurs comptes Google Drive, OneDrive et Dropbox. Chaque compte peut servir de source ou de destination.",
      connect: "Connecter un compte",
      connectGoogle: "Google Drive",
      connectMicrosoft: "OneDrive (Microsoft)",
      connectDropbox: "Dropbox",
      primary: "Principal",
      noAccounts: "Aucun compte connecté.",
      remove: "Retirer",
      confirmRemove: "Retirer ce compte ? Les sauvegardes liées seront affectées.",
      providerGoogle: "Google Drive",
      providerMicrosoft: "OneDrive",
      providerDropbox: "Dropbox",
      tabDrive: "Comptes Drive",
      tabS3: "Comptes S3",
      msAdminLabel: "Microsoft 365 Entreprise",
      msAdminConsent: "Pré-autoriser pour mon organisation",
      msAdminConsentHint: "Admin Microsoft 365 uniquement. Une seule fois, débloque tous vos utilisateurs.",
      msAdminConsentOk: "Pré-autorisation Microsoft réussie. Vos utilisateurs peuvent maintenant connecter leur OneDrive sans blocage.",
      msAdminConsentError: "Échec de la pré-autorisation Microsoft :",
      welcomeTitle: "Bienvenue sur SAVEBOX",
      welcomeBody: "Pour commencer, connectez les comptes Google Drive, OneDrive ou Dropbox que vous souhaitez utiliser comme source ou destination de vos sauvegardes. Vous pouvez en ajouter autant que vous voulez — ils peuvent être différents de votre compte de connexion.",
    },
    picker: {
      title: "Sélectionnez un dossier Google Drive à sauvegarder",
      browse: "Parcourir Google Drive",
      selectedLabel: "Dossier sélectionné",
      noneSelected: "Aucun dossier sélectionné",
      hint: "Cliquez sur \"Parcourir Google Drive\" pour ouvrir le sélecteur officiel Google. Sélectionnez un dossier, son contenu (y compris les sous-dossiers) sera sauvegardé.",
      noAccount: "Sélectionnez d'abord un compte Google Drive.",
      loading: "Le sélecteur Google est en cours de chargement, réessayez dans 2 secondes.",
      noKey: "Sélecteur Google non configuré côté serveur (GOOGLE_PICKER_API_KEY manquante).",
    },
    s3: {
      subtitle: "Stockage objet S3-compatible (Wasabi). Utilisez vos clés d'accès — testées avant enregistrement.",
      add: "Ajouter un compte Wasabi",
      addTitle: "Nouveau compte Wasabi",
      empty: "Aucun compte S3 configuré",
      emptyHint: "Connectez un bucket Wasabi pour pouvoir l'utiliser comme destination de vos sauvegardes.",
      fieldName: "Nom (libre)",
      fieldNamePh: "Backup prod EU",
      fieldBucket: "Nom du bucket",
      fieldRegion: "Région",
      fieldAccessKey: "Access Key",
      fieldSecretKey: "Secret Key",
      test: "Tester la connexion",
      save: "Enregistrer",
      saved: "Compte S3 enregistré.",
      removed: "Compte S3 supprimé.",
      removeTitle: "Supprimer ce compte S3 ?",
      removeConfirm: "Cette action ne peut être annulée. Les jobs utilisant ce compte ne pourront plus s'exécuter.",
      connected: "Connexion réussie",
      testOk: "Connexion validée — bucket accessible.",
      security: "Vos clés sont chiffrées au repos (Fernet) et utilisées uniquement pour exécuter vos jobs de sauvegarde. Elles ne sont jamais affichées en clair après enregistrement.",
    },
    wizard: {
      title: "Nouvelle sauvegarde",
      editTitle: "Modifier la sauvegarde",
      guardTitle: "Prérequis",
      guardNoneHeading: "Aucun compte cloud connecté",
      guardNoneBody: "Pour créer une sauvegarde, vous devez d'abord connecter au moins deux comptes cloud : un compte source (où sont vos fichiers) et un compte destination (où ils seront copiés). Vous pouvez librement mixer Google Drive, OneDrive et Dropbox.",
      guardOneHeading: "Un seul compte connecté",
      guardOneBody: "Il vous faut au moins deux comptes cloud connectés pour créer une sauvegarde : une source et une destination. Connectez un second compte (Google Drive, OneDrive ou Dropbox) pour continuer.",
      guardCta: "Connecter un compte",
      guardBack: "Retour au tableau de bord",
      step1: "Identité",
      step2: "Source",
      step3: "Destination",
      step4: "Planification",
      step5: "Options",
      jobName: "Nom de la sauvegarde",
      jobNamePlaceholder: "Sauvegarde mensuelle des contrats",
      sourceTitle: "Compte source (Drive A)",
      destTitle: "Compte de destination (Drive B)",
      pickAccount: "Choisir un compte",
      pickFolder: "Choisir un dossier",
      destStepLabel: "03 — Destination",
      destType: "Type de destination",
      destTypeDrive: "Compte Drive",
      destTypeDriveHint: "Google Drive, OneDrive ou Dropbox",
      destTypeS3: "Compte S3",
      destTypeS3Hint: "Wasabi (backup froid)",
      noS3Accounts: "Aucun compte S3 configuré",
      noS3AccountsHint: "Ajoutez d'abord un compte Wasabi dans la page Comptes → onglet S3.",
      addS3Account: "Ajouter un compte S3",
      s3Bucket: "Compte / Bucket Wasabi",
      s3BucketPick: "Sélectionnez un bucket",
      s3Prefix: "Préfixe S3 (optionnel)",
      s3PrefixPlaceholder: "savebox/mon-job/",
      s3PrefixHint: "Tous les fichiers seront stockés sous ce préfixe dans le bucket. Permet de séparer plusieurs jobs dans le même bucket.",
      selectedFolder: "Dossier sélectionné",
      frequency: "Fréquence",
      frequencyManual: "Manuelle",
      frequencyHourly: "Toutes les heures",
      frequencyDaily: "Quotidienne",
      frequencyWeekly: "Hebdomadaire",
      frequencyMonthly: "Mensuelle",
      time: "Heure",
      dayOfWeek: "Jour de la semaine",
      dayOfMonth: "Jour du mois",
      timezone: "Fuseau horaire",
      filters: "Filtres",
      includeExtensions: "Extensions à inclure (vide = toutes)",
      excludeExtensions: "Extensions à exclure",
      maxSize: "Taille max par fichier (Mo)",
      excludeFolders: "Dossiers à ignorer (noms)",
      incremental: "Mode incrémental",
      incrementalDesc: "Ne copier que les fichiers modifiés (recommandé).",
      versioning: "Conserver les versions",
      versioningDesc: "Garder les anciennes versions lors des écrasements.",
      enabled: "Activer immédiatement",
      create: "Créer la sauvegarde",
      update: "Mettre à jour",
      monday: "Lundi",
      tuesday: "Mardi",
      wednesday: "Mercredi",
      thursday: "Jeudi",
      friday: "Vendredi",
      saturday: "Samedi",
      sunday: "Dimanche",
    },
    jobDetail: {
      historyTitle: "Historique des exécutions",
      noRuns: "Aucune exécution pour le moment.",
      duration: "Durée",
      trigger: "Déclencheur",
      triggerManual: "Manuelle",
      triggerScheduled: "Planifiée",
      logsTitle: "Journaux",
      noLogs: "Pas de journal disponible.",
      deleteJob: "Supprimer la sauvegarde",
      confirmDelete: "Supprimer définitivement cette sauvegarde et tout son historique ?",
      summary: "Résumé",
      sourceLabel: "Source",
      destLabel: "Destination",
      scheduleLabel: "Planification",
      filtersLabel: "Filtres",
      optionsLabel: "Options",
    },
    settings: {
      title: "Paramètres",
      language: "Langue de l'interface",
      french: "Français",
      english: "English",
      account: "Compte",
      saved: "Préférences enregistrées",
    },
    pricing: {
      tagline: "TARIFS",
      title: "Choisissez votre rythme.",
      subtitle: "Pay-as-you-need. Sans engagement. Le plan se renouvelle tous les 30 jours.",
      perMonth: "/ mois",
      free: "Gratuit",
      mostPopular: "POPULAIRE",
      currentPlan: "Plan actuel",
      cta: "Choisir ce plan",
      ctaCurrent: "Votre plan",
      ctaFree: "Commencer gratuitement",
      upgrading: "Redirection vers Stripe…",
      success: "Paiement réussi — plan activé",
      failed: "Le paiement n'a pas abouti",
      checking: "Vérification du paiement…",
      planLabel: "Plan",
      usage: "Consommation",
      usageOf: "sur",
      unlimited: "illimité",
      renewsOn: "Renouvelable le",
      upgradeNow: "Mettre à niveau",
      manage: "Gérer mon plan",
      backToDashboard: "Retour au tableau de bord",
      trialBadge: "14 JOURS GRATUITS",
      tryPro: "Essayer Pro 14 jours",
      trialActive: "Essai en cours",
      trialUsed: "Essai déjà utilisé",
      trialActivated: "Essai Pro activé — 14 jours offerts",
      noCreditCard: "Sans carte bancaire",
      addonsTitle: "Pack migration",
      addonsSubtitle: "Vous changez d'écosystème ? Transférez tout, une fois, en illimité.",
      migrationDuration: "7 jours d'accès",
      migrationCta: "Lancer ma migration",
      oneShot: "Paiement unique",
    },
    admin: {
      title: "Back-office",
      subtitle: "Pilotez SAVEBOX — utilisateurs, paiements, sauvegardes.",
      nav: {
        overview: "Vue d'ensemble",
        users: "Utilisateurs",
        plans: "Plans",
        payments: "Paiements",
        jobs: "Sauvegardes",
        settings: "Configuration",
        emails: "Emails",
        legal: "Légal",
        audit: "Audit",
        system: "Système",
      },
      adminOnly: "Réservé aux administrateurs",
      stats: {
        users: "Utilisateurs",
        paying: "Clients payants",
        trial: "En essai",
        new7d: "Nouveaux (7j)",
        revenueTotal: "Revenus totaux",
        revenue30d: "Revenus 30j",
        revenue7d: "Revenus 7j",
        jobs: "Sauvegardes",
        runs: "Exécutions",
        failedRuns: "Échecs (7j)",
        accounts: "Comptes Drive",
        bytesTotal: "Données transférées",
        filesTotal: "Fichiers copiés",
        planDistribution: "Distribution par plan",
        recentRuns: "Exécutions récentes",
      },
      users: {
        search: "Rechercher (email / nom)…",
        filterPlan: "Filtrer par plan",
        all: "Tous",
        empty: "Aucun utilisateur",
        joinedOn: "Inscrit le",
        plan: "Plan",
        usage: "Usage",
        actions: "Actions",
        view: "Voir",
        grantPlan: "Accorder un plan",
        grantDays: "Jours",
        grantBtn: "Activer",
        resetUsage: "Réinitialiser l'usage",
        deleteUser: "Supprimer l'utilisateur",
        confirmDelete: "Supprimer définitivement cet utilisateur et toutes ses données ?",
        toggleAdmin: "Admin",
        toggleActive: "Actif",
        notes: "Notes internes",
        saveNotes: "Enregistrer",
        savedNotes: "Notes enregistrées",
      },
      payments: {
        empty: "Aucune transaction",
        date: "Date",
        user: "Utilisateur",
        amount: "Montant",
        plan: "Plan",
        status: "Statut",
        session: "Session Stripe",
        all: "Toutes",
        paid: "Payé",
        pending: "En attente",
        unpaid: "Non payé",
      },
      jobs: {
        empty: "Aucune sauvegarde",
        owner: "Propriétaire",
        name: "Nom",
        status: "Statut",
        files: "Fichiers",
        size: "Taille",
        lastRun: "Dernière",
      },
      system: {
        title: "Configuration système",
        demoMode: "Mode démo",
        oauth: "Google OAuth",
        stripe: "Stripe",
        adminEmails: "Emails administrateurs",
        frontendUrl: "URL frontend",
        redirectUri: "Redirect URI OAuth",
        serverTime: "Heure serveur",
        configured: "Configuré",
        notConfigured: "Non configuré",
        enabled: "Actif",
        disabled: "Inactif",
      },
    },
    cookie: {
      title: "Cookies & confidentialité",
      body: "Nous utilisons uniquement des cookies essentiels au fonctionnement du service (session, préférence de langue). Aucun traceur publicitaire.",
      learnMore: "En savoir plus",
      accept: "J'accepte",
      reject: "Refuser",
    },
  },
  en: {
    nav: {
      features: "Features",
      how: "How it works",
      pricing: "Pricing",
      faq: "FAQ",
      login: "Sign in",
      dashboard: "Dashboard",
      logout: "Sign out",
      accounts: "Drive accounts",
      settings: "Settings",
      jobs: "Backups",
      billing: "Plan & billing",
      admin: "Back-office",
    },
    common: {
      loading: "Loading…",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      create: "Create",
      back: "Back",
      next: "Next",
      previous: "Previous",
      page: "Page",
      yes: "Yes",
      no: "No",
      confirm: "Confirm",
      none: "None",
      search: "Search",
      copied: "Copied",
      skipped: "Skipped",
      failed: "Failed",
      success: "Success",
      partial: "Partial",
      running: "Running",
      idle: "Idle",
      enabled: "Enabled",
      disabled: "Disabled",
      enable: "Enable",
      disable: "Disable",
      open: "Open",
      close: "Close",
      view: "View",
      browse: "Browse",
      root: "Drive root",
    },
    landing: {
      tagline: "CLOUD-TO-CLOUD BACKUP INFRASTRUCTURE",
      heroTitle: "Automate Your Cloud Infrastructure.",
      heroSubtitle:
        "Set-and-forget cloud backups across Google Drive, OneDrive and Dropbox. Incremental, scheduled, and unconditionally secure — without keeping a PC powered on.",
      ctaPrimary: "Start syncing",
      ctaSecondary: "See features",
      featuresTitle: "Precision engineering for your data",
      featuresSubtitle:
        "SAVEBOX moves your files from one cloud to another with clockwork accuracy. Google Drive, OneDrive, Dropbox, Wasabi S3 — no middleman.",
      f1Title: "Zero-touch scheduling",
      f1Desc:
        "Run backups hourly, daily, weekly, or monthly. No need for a powered-on machine — our server handles it.",
      f2Title: "Incremental copy",
      f2Desc:
        "Only modified files are transferred. MD5 fingerprints compared between source and destination for maximum efficiency.",
      f3Title: "Smart versioning",
      f3Desc:
        "Keep previous versions on overwrite. Your data is historized, never lost.",
      f4Title: "Precise filters",
      f4Desc:
        "Filter by extension, size, or folder name. Back up exactly what matters.",
      f5Title: "Direct cloud-to-cloud",
      f5Desc:
        "Server-to-server transfers between Google Drive, OneDrive, Dropbox and Wasabi S3. No data lands on your machine.",
      f6Title: "Complete history",
      f6Desc:
        "Detailed logs for each run. Files copied, skipped, failed, and bytes transferred.",
      howTitle: "Three steps. Everything else is automatic.",
      step1: "Connect your cloud accounts",
      step1Desc: "Link your Google Drive, OneDrive or Dropbox accounts with one OAuth click. Mix sources and destinations as you wish.",
      step2: "Configure the backup",
      step2Desc: "Pick folders, frequency (hourly, daily, weekly, monthly), and filters.",
      step3: "Let it run. That's it.",
      step3Desc: "SAVEBOX executes your jobs in the background. History lands in your dashboard.",
      ctaBottomTitle: "Ready to turn off your PC?",
      ctaBottomSubtitle: "Get started with SAVEBOX in under two minutes.",
      faqTitle: "Frequently asked questions",
      faq1Q: "Do my files travel through your server?",
      faq1A:
        "File content is temporarily downloaded by our server to be re-uploaded to the destination. No data is retained after transfer.",
      faq2Q: "Do backups consume my cloud storage quota?",
      faq2A:
        "Yes — your destination account (Drive B, OneDrive, Dropbox or Wasabi bucket) must have enough free space to host the files from your source account.",
      faq3Q: "Which cloud services are supported?",
      faq3A:
        "Today: Google Drive, Microsoft OneDrive, Dropbox and Wasabi (S3 storage). You can freely mix sources and destinations across these services.",
      faq4Q: "What happens when a file changes?",
      faq4A:
        "In incremental mode, SAVEBOX detects changes via MD5 checksum and only updates the modified files.",
      footerRights: "All rights reserved.",
    },
    auth: {
      signInGoogle: "Continue with Google",
      signInMicrosoft: "Continue with Microsoft",
      signInDemo: "Try demo mode",
      signingIn: "Signing in…",
      welcome: "Welcome to SAVEBOX",
      welcomeSub: "Sign in to manage your cloud-to-cloud backups.",
      oauthDisabled:
        "OAuth is not configured yet. Use demo mode to explore the interface.",
      orSeparator: "or",
      providerHint: "Pick your main provider — you'll be able to connect the other cloud later.",
    },
    dashboard: {
      title: "Dashboard",
      subtitle: "Pilot all your backups at a glance.",
      newJob: "New backup",
      noJobs: "No backup configured yet.",
      noJobsSub: "Create your first cloud-to-cloud backup.",
      jobs: "Jobs",
      activeJobs: "Active jobs",
      accounts: "Accounts",
      totalFiles: "Files backed up",
      totalData: "Data transferred",
      recentActivity: "Recent activity",
      noActivity: "No activity yet.",
      runNow: "Run now",
      viewDetails: "Details",
      lastRun: "Last run",
      nextRun: "Next run",
      never: "Never",
      now: "Just now",
      onDemand: "On demand",
    },
    accountsPage: {
      title: "Cloud accounts",
      subtitle: "Connect multiple Google Drive, OneDrive and Dropbox accounts. Each one can be used as source or destination.",
      connect: "Connect an account",
      connectGoogle: "Google Drive",
      connectMicrosoft: "OneDrive (Microsoft)",
      connectDropbox: "Dropbox",
      primary: "Primary",
      noAccounts: "No accounts connected yet.",
      remove: "Remove",
      confirmRemove: "Remove this account? Linked backups will be affected.",
      providerGoogle: "Google Drive",
      providerMicrosoft: "OneDrive",
      providerDropbox: "Dropbox",
      tabDrive: "Drive accounts",
      tabS3: "S3 accounts",
      msAdminLabel: "Microsoft 365 Business",
      msAdminConsent: "Pre-authorize for my organization",
      msAdminConsentHint: "Microsoft 365 admin only. One click, unlocks all your users.",
      msAdminConsentOk: "Microsoft pre-authorization successful. Your users can now connect their OneDrive without admin approval.",
      msAdminConsentError: "Microsoft pre-authorization failed:",
      welcomeTitle: "Welcome to SAVEBOX",
      welcomeBody: "To get started, connect the Google Drive, OneDrive or Dropbox accounts you want to use as source or destination for your backups. You can add as many as you want — they can be different from the account you used to sign in.",
    },
    picker: {
      title: "Select a Google Drive folder to back up",
      browse: "Browse Google Drive",
      selectedLabel: "Selected folder",
      noneSelected: "No folder selected",
      hint: "Click \"Browse Google Drive\" to open the official Google picker. Pick a folder — its contents (including subfolders) will be backed up.",
      noAccount: "Select a Google Drive account first.",
      loading: "Google picker is still loading, please retry in 2 seconds.",
      noKey: "Google picker not configured on the server (GOOGLE_PICKER_API_KEY missing).",
    },
    s3: {
      subtitle: "S3-compatible object storage (Wasabi). Provide your access keys — tested before storage.",
      add: "Add a Wasabi account",
      addTitle: "New Wasabi account",
      empty: "No S3 accounts configured",
      emptyHint: "Connect a Wasabi bucket to use it as a backup destination.",
      fieldName: "Display name",
      fieldNamePh: "Backup prod EU",
      fieldBucket: "Bucket name",
      fieldRegion: "Region",
      fieldAccessKey: "Access Key",
      fieldSecretKey: "Secret Key",
      test: "Test connection",
      save: "Save",
      saved: "S3 account saved.",
      removed: "S3 account removed.",
      removeTitle: "Remove this S3 account?",
      removeConfirm: "This action cannot be undone. Jobs using this account will stop working.",
      connected: "Connection successful",
      testOk: "Connection validated — bucket accessible.",
      security: "Your keys are encrypted at rest (Fernet) and only used to execute your backup jobs. They are never shown in plain text after saving.",
    },
    wizard: {
      title: "New backup",
      editTitle: "Edit backup",
      guardTitle: "Requirements",
      guardNoneHeading: "No cloud accounts connected",
      guardNoneBody: "To create a backup, you first need to connect at least two cloud accounts: a source (where your files are) and a destination (where they will be copied). You can freely mix Google Drive, OneDrive and Dropbox.",
      guardOneHeading: "Only one account connected",
      guardOneBody: "You need at least two connected cloud accounts to create a backup: a source and a destination. Please connect a second account (Google Drive, OneDrive or Dropbox) to continue.",
      guardCta: "Connect an account",
      guardBack: "Back to dashboard",
      step1: "Identity",
      step2: "Source",
      step3: "Destination",
      step4: "Schedule",
      step5: "Options",
      jobName: "Backup name",
      jobNamePlaceholder: "Monthly contracts backup",
      sourceTitle: "Source account (Drive A)",
      destTitle: "Destination account (Drive B)",
      pickAccount: "Pick an account",
      pickFolder: "Pick a folder",
      destStepLabel: "03 — Destination",
      destType: "Destination type",
      destTypeDrive: "Drive account",
      destTypeDriveHint: "Google Drive, OneDrive or Dropbox",
      destTypeS3: "S3 account",
      destTypeS3Hint: "Wasabi (cold backup)",
      noS3Accounts: "No S3 accounts configured",
      noS3AccountsHint: "First add a Wasabi account from the Accounts page → S3 tab.",
      addS3Account: "Add an S3 account",
      s3Bucket: "Wasabi account / bucket",
      s3BucketPick: "Select a bucket",
      s3Prefix: "S3 prefix (optional)",
      s3PrefixPlaceholder: "savebox/my-job/",
      s3PrefixHint: "All files will be stored under this prefix in the bucket. Useful to separate multiple jobs in the same bucket.",
      selectedFolder: "Selected folder",
      frequency: "Frequency",
      frequencyManual: "Manual",
      frequencyHourly: "Hourly",
      frequencyDaily: "Daily",
      frequencyWeekly: "Weekly",
      frequencyMonthly: "Monthly",
      time: "Time",
      dayOfWeek: "Day of week",
      dayOfMonth: "Day of month",
      timezone: "Timezone",
      filters: "Filters",
      includeExtensions: "Extensions to include (empty = all)",
      excludeExtensions: "Extensions to exclude",
      maxSize: "Max file size (MB)",
      excludeFolders: "Folders to skip (names)",
      incremental: "Incremental mode",
      incrementalDesc: "Only copy modified files (recommended).",
      versioning: "Keep versions",
      versioningDesc: "Preserve old versions when overwriting.",
      enabled: "Enable immediately",
      create: "Create backup",
      update: "Update",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    },
    jobDetail: {
      historyTitle: "Run history",
      noRuns: "No runs yet.",
      duration: "Duration",
      trigger: "Trigger",
      triggerManual: "Manual",
      triggerScheduled: "Scheduled",
      logsTitle: "Logs",
      noLogs: "No logs available.",
      deleteJob: "Delete backup",
      confirmDelete: "Permanently delete this backup and its full history?",
      summary: "Summary",
      sourceLabel: "Source",
      destLabel: "Destination",
      scheduleLabel: "Schedule",
      filtersLabel: "Filters",
      optionsLabel: "Options",
    },
    settings: {
      title: "Settings",
      language: "Interface language",
      french: "Français",
      english: "English",
      account: "Account",
      saved: "Preferences saved",
    },
    pricing: {
      tagline: "PRICING",
      title: "Pick your pace.",
      subtitle: "Pay-as-you-need. No commitment. Plans renew every 30 days.",
      perMonth: "/ month",
      free: "Free",
      mostPopular: "POPULAR",
      currentPlan: "Current plan",
      cta: "Choose this plan",
      ctaCurrent: "Your plan",
      ctaFree: "Start free",
      upgrading: "Redirecting to Stripe…",
      success: "Payment successful — plan activated",
      failed: "Payment did not complete",
      checking: "Verifying payment…",
      planLabel: "Plan",
      usage: "Usage",
      usageOf: "of",
      unlimited: "unlimited",
      renewsOn: "Renews on",
      upgradeNow: "Upgrade",
      manage: "Manage plan",
      backToDashboard: "Back to dashboard",
      trialBadge: "14-DAY FREE TRIAL",
      tryPro: "Try Pro for 14 days",
      trialActive: "Trial active",
      trialUsed: "Trial already used",
      trialActivated: "Pro trial activated — 14 days free",
      noCreditCard: "No credit card",
      addonsTitle: "Migration pack",
      addonsSubtitle: "Switching ecosystems? Transfer everything, once, unlimited.",
      migrationDuration: "7-day window",
      migrationCta: "Start my migration",
      oneShot: "One-time payment",
    },
    admin: {
      title: "Back-office",
      subtitle: "Pilot SAVEBOX — users, payments, backups.",
      nav: {
        overview: "Overview",
        users: "Users",
        plans: "Plans",
        payments: "Payments",
        jobs: "Backups",
        settings: "Settings",
        emails: "Emails",
        legal: "Legal",
        audit: "Audit",
        system: "System",
      },
      adminOnly: "Admins only",
      stats: {
        users: "Users",
        paying: "Paying customers",
        trial: "On trial",
        new7d: "New (7d)",
        revenueTotal: "Total revenue",
        revenue30d: "Revenue 30d",
        revenue7d: "Revenue 7d",
        jobs: "Backups",
        runs: "Runs",
        failedRuns: "Failures (7d)",
        accounts: "Drive accounts",
        bytesTotal: "Data transferred",
        filesTotal: "Files copied",
        planDistribution: "Plan distribution",
        recentRuns: "Recent runs",
      },
      users: {
        search: "Search (email / name)…",
        filterPlan: "Filter by plan",
        all: "All",
        empty: "No users",
        joinedOn: "Joined on",
        plan: "Plan",
        usage: "Usage",
        actions: "Actions",
        view: "View",
        grantPlan: "Grant a plan",
        grantDays: "Days",
        grantBtn: "Activate",
        resetUsage: "Reset usage",
        deleteUser: "Delete user",
        confirmDelete: "Permanently delete this user and all their data?",
        toggleAdmin: "Admin",
        toggleActive: "Active",
        notes: "Internal notes",
        saveNotes: "Save",
        savedNotes: "Notes saved",
      },
      payments: {
        empty: "No transactions",
        date: "Date",
        user: "User",
        amount: "Amount",
        plan: "Plan",
        status: "Status",
        session: "Stripe session",
        all: "All",
        paid: "Paid",
        pending: "Pending",
        unpaid: "Unpaid",
      },
      jobs: {
        empty: "No backups",
        owner: "Owner",
        name: "Name",
        status: "Status",
        files: "Files",
        size: "Size",
        lastRun: "Last run",
      },
      system: {
        title: "System configuration",
        demoMode: "Demo mode",
        oauth: "Google OAuth",
        stripe: "Stripe",
        adminEmails: "Admin emails",
        frontendUrl: "Frontend URL",
        redirectUri: "OAuth redirect URI",
        serverTime: "Server time",
        configured: "Configured",
        notConfigured: "Not configured",
        enabled: "Enabled",
        disabled: "Disabled",
      },
    },
    cookie: {
      title: "Cookies & privacy",
      body: "We only use cookies essential to the service (session, language preference). No advertising trackers.",
      learnMore: "Learn more",
      accept: "I accept",
      reject: "Decline",
    },
  },
};

const I18nContext = createContext({ lang: "fr", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("savebox_lang") || "fr");

  const setLang = useCallback((l) => {
    localStorage.setItem("savebox_lang", l);
    setLangState(l);
  }, []);

  const t = useCallback(
    (path) => {
      const parts = path.split(".");
      let cur = translations[lang];
      for (const p of parts) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, p)) {
          cur = cur[p];
        } else {
          return path;
        }
      }
      return cur;
    },
    [lang]
  );

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
